import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { faCloudArrowUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Input,
    Box as MuiBox,
    styled,
    Theme,
    Typography,
    TypographyProps,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Enum_Border_Style } from "src/api/category/enum";
import { MapContext } from "src/contexts/map";

enum Enum_Upload_Status {
    UPLOADING = "uploading",
    DONE = "done",
}

const Box = styled(MuiBox)<{ isdragover: string; ismobile: string }>(({
    theme,
    isdragover,
    ismobile,
}) => {
    const isMobile = ismobile === "true";
    return {
        border: `${theme.spacing(0.2)} ${isdragover === "true" || isMobile ? Enum_Border_Style.SOLID : Enum_Border_Style.DASHED} ${theme.palette.secondary.main}`,
        width: isMobile ? theme.spacing(20) : "auto",
        borderRadius: isMobile ? theme.spacing(0.5) : theme.spacing(0),
        padding: isMobile ? theme.spacing(0.5) : theme.spacing(4),
        textAlign: "center",
        cursor: "pointer",
        position: "relative",
    };
});

const LoadingTypography = styled(Typography)<TypographyProps>(({ theme }) => ({
    color: theme.palette.common.white,
    width: theme.spacing(3),
    height: theme.spacing(3),
    borderRadius: "50%",
    textAlign: "center",
    marginTop: "auto",
    fontSize: "0.8rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
}));

const UnderlinedTypography = styled(Typography)<TypographyProps>(() => ({
    textDecoration: "underline",
}));

const Icon = styled(FontAwesomeIcon)(({ theme }) => ({
    color: theme.palette.secondary.main,
}));

const DeleteIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    minWidth: theme.spacing(2),
}));

const File = () => {
    const { newElement, updateNewElement } = useContext(MapContext);

    const [isDragOver, setIsDragOver] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<
        {
            id?: number;
            file: { name: string };
            status: Enum_Upload_Status; // used to detect the file upload status
            // when it's uploaded, the delete option becomes apparent and the LoadingTypography background color changes
        }[]
    >([]);

    const { t } = useTranslation();

    useEffect(() => {
        if (newElement?.files && selectedFiles.length === 0) {
            const initialFiles = newElement.files.map((file) => ({
                file: {
                    name: file.name,
                },
                status: Enum_Upload_Status.DONE,
            }));
            setSelectedFiles(initialFiles);
        }
    }, [newElement?.files]);

    const handleDrag = (value: boolean) => {
        setIsDragOver(value);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);

        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) {
            processFiles(files);
        }
    };

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (event.target.files) {
            const files = Array.from(event.target.files);
            processFiles(files);
        }
    };

    // files are converted to base64 and saved in minio
    const convertFileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve(reader.result as string);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const processFiles = async (files: File[]) => {
        const updatedFiles = await Promise.all(
            files.map(async (file) => {
                const base64 = await convertFileToBase64(file);
                return {
                    file,
                    base64,
                    status: Enum_Upload_Status.DONE,
                };
            }),
        );

        setSelectedFiles((prev) => [
            ...prev,
            ...updatedFiles.map(({ file }) => ({
                file,
                status: Enum_Upload_Status.DONE,
            })),
        ]);

        // the processed file is saved to context
        // the file will be saved in s3 when creating the element in the element service
        updateNewElement({
            ...newElement!,
            files: [
                ...(newElement?.files || []),
                ...updatedFiles.map(({ file, base64 }) => ({
                    name: file.name,
                    base64,
                })),
            ],
        });
    };

    const deleteDocument = (fileToDelete: {
        file: File;
        status: Enum_Upload_Status;
    }) => {
        const updatedFiles = selectedFiles.filter(
            (fileData) => fileData.file.name !== fileToDelete.file.name,
        );

        setSelectedFiles(updatedFiles);

        updateNewElement({
            ...newElement!,
            files: updatedFiles.map((fileData) => ({
                id: fileData.id,
                name: fileData.file.name,
                base64:
                    newElement?.files && newElement?.files.length > 0
                        ? newElement?.files.find(
                              (f) => f.name === fileData.file.name,
                          )?.base64 || ""
                        : "",
            })),
        });
    };

    const theme = useTheme();

    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("md"),
    );

    const Label = styled("label")(() => ({
        cursor: "pointer",
        color: "inherit",
        [theme.breakpoints.down("md")]: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
    }));

    return (
        <>
            <Box
                isdragover={isDragOver.toString()}
                ismobile={isMobile.toString()}
                onDragOver={(event) => {
                    event.preventDefault();
                    handleDrag(true);
                }}
                onDragLeave={() => handleDrag(false)}
                onDrop={handleDrop}
            >
                <>
                    <Input
                        id="file-upload"
                        type="file"
                        inputProps={{
                            multiple: true,
                            accept: ".jpg, .jpeg, .png, .pdf, .docx",
                        }}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />
                    {!isMobile && <Icon icon={faCloudArrowUp} />}
                    <Typography color="InfoText" mt={0.5}>
                        <Label htmlFor="file-upload">
                            {isMobile ? (
                                <>
                                    <FontAwesomeIcon icon={faCloudArrowUp} />
                                    <Typography
                                        component="span"
                                        color="secondary.dark"
                                    >
                                        {t("upload")}
                                    </Typography>
                                </>
                            ) : (
                                <UnderlinedTypography
                                    component="span"
                                    color="primary"
                                >
                                    {t("select_file")}
                                </UnderlinedTypography>
                            )}
                        </Label>
                    </Typography>
                    {!isMobile && (
                        <Typography mt={0.5} color="secondary.main">
                            {t("file_format")}
                        </Typography>
                    )}
                </>
            </Box>

            {selectedFiles.length > 0 && (
                <MuiBox textAlign="left" mt={1}>
                    {selectedFiles.map((fileData, index) => (
                        <MuiBox
                            key={index}
                            display="flex"
                            alignItems="center"
                            mb={0.5}
                        >
                            <Typography flex={1}>
                                {fileData.file.name}{" "}
                            </Typography>

                            <LoadingTypography
                                sx={{
                                    backgroundColor:
                                        fileData.status ===
                                        Enum_Upload_Status.UPLOADING
                                            ? "primary.main"
                                            : "secondary.main",
                                }}
                                variant="body2"
                            >
                                {fileData.status ===
                                Enum_Upload_Status.UPLOADING ? (
                                    t("uploading")
                                ) : (
                                    <DeleteIcon
                                        onClick={() =>
                                            deleteDocument(
                                                fileData as unknown as {
                                                    file: File;
                                                    status: Enum_Upload_Status;
                                                },
                                            )
                                        }
                                        icon={faTimes}
                                    />
                                )}
                            </LoadingTypography>
                        </MuiBox>
                    ))}
                </MuiBox>
            )}
        </>
    );
};

export default File;
